var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue";
import ViewAppointment from '@/components/ViewAppointment/ViewAppointment.vue';
import { GHLLanguageKeysMapper } from '@/helper/const';
import Calendar from '@/models/calendar';
import CalendarEvent from '@/models/calendar_event';
import Contact from '@/models/contact';
import User from '@/models/user';
import { setupVueI18n } from '@/plugin/vue-i18n';
import { CalendarEventService } from '@/service/CalendarEventService';
import { ContactService } from '@/service/ContactService';
import { UserService } from '@/service/UserService';
import AppState, { getEquipmentsInLocation, getRoomsInLocation, getTargetTimezone, setInitialValues, setLocationId, } from '@/states/view_appointment';
import { UIContentWrap } from '@gohighlevel/ghl-ui';
import '@gohighlevel/ghl-ui/dist/style.css';
import moment from 'moment';
import { onMounted, provide, ref } from 'vue';
import '../index.css';
import BookAppointment from './BookAppointment.vue';
import ViewActivityLog from './ViewActivityLog.vue';
export default /*#__PURE__*/ _defineComponent({
    __name: 'ViewAppointment',
    props: {
        route: Object,
        router: Object,
        showModal: Boolean,
        onCancelClick: Function,
        data: null,
        locale: String,
    },
    setup(__props) {
        const props = __props;
        setupVueI18n(props.locale);
        moment.locale();
        // inject current route
        provide('route', props.route);
        provide('router', props.router);
        const appointmentStatus = ref('confirmed');
        const isLoading = ref(false);
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const parsedData = JSON.parse(props.data);
            setInitialValues(parsedData);
            setLocationId(props.route.params.location_id);
            yield fetchEventDetails();
            if ((_b = (_a = AppState.appointmentDetail) === null || _a === void 0 ? void 0 : _a.assignedResources) === null || _b === void 0 ? void 0 : _b.length) {
                yield Promise.all([getRoomsInLocation(), getEquipmentsInLocation()]);
            }
        }));
        // Function to fetch all the data that is required to view the appointment details
        const fetchEventDetails = () => __awaiter(this, void 0, void 0, function* () {
            isLoading.value = true;
            const targetTimezone = getTargetTimezone();
            const { data } = yield CalendarEventService.getEventDetails(AppState.eventId, targetTimezone);
            // Create a new CalendarEvent object from the fetched data and store it in 'appointmentDetail.value'.
            AppState.appointmentDetail = new CalendarEvent(data.appointment);
            const appointmentDetail = AppState.appointmentDetail;
            appointmentStatus.value = AppState.appointmentDetail.appointmentStatus;
            // Fetch additional details related to the calendar event in parallel.
            yield Promise.all([
                fetchCalendarDetails(appointmentDetail.calendarId),
                fetchContactDetails(appointmentDetail.contactId),
                fetchUserDetails(appointmentDetail.bookedBy),
                fetchAppointmentOwnerDetails(appointmentDetail.assignedUserId),
                fetchGuestsDetail(appointmentDetail.guests),
                fetchContactAppointmentDetails(AppState.receivedData.appointmentIds),
            ]);
            isLoading.value = false;
        });
        // Function to fetch details of the appointment owner (user) based on the provided userId.
        const fetchAppointmentOwnerDetails = (userId) => __awaiter(this, void 0, void 0, function* () {
            if (!userId) {
                return;
            }
            // Make an API request to get the user details and store it in 'appointmentOwnerDetails.value'.
            try {
                const { data } = yield UserService.getUserDetails(userId);
                AppState.appointmentOwnerDetails = new User(data);
            }
            catch (error) {
                AppState.appointmentOwnerDetails = undefined;
            }
        });
        // Function to fetch details of the calendar based on the provided calendarId.
        const fetchCalendarDetails = (calendarId) => __awaiter(this, void 0, void 0, function* () {
            if (!calendarId) {
                return;
            }
            // Make an API request to get the calendar details and store it in 'calendarDetail.value'.
            const { data } = yield CalendarEventService.getCalendarDetails(calendarId);
            AppState.calendarDetail = new Calendar(data.calendar);
        });
        // Function to fetch details of a contact based on the provided contactId.
        const fetchContactDetails = (contactId) => __awaiter(this, void 0, void 0, function* () {
            if (!contactId) {
                return;
            }
            // Adding try catch when contact API returns 401 hen assigned_data turned on
            try {
                // Make an API request to get the contact details and store it in 'contactDetails.value'.
                const { data } = yield ContactService.getContactDetails(contactId);
                AppState.contactDetails = new Contact(data.contact);
            }
            catch (err) {
                AppState.contactDetails = undefined;
            }
        });
        // Function to fetch details of a user based on the provided userId.
        const fetchUserDetails = (userId) => __awaiter(this, void 0, void 0, function* () {
            if (!userId) {
                return;
            }
            try {
                // Make an API request to get the user details and store it in 'userDetails.value'.
                const { data } = yield UserService.getUserDetails(userId);
                AppState.userDetails = new User(data);
            }
            catch (error) {
                AppState.userDetails = undefined;
            }
        });
        // Function to fetch details of guests associated with the appointment based on guestIds.
        const fetchGuestsDetail = (guestIds) => __awaiter(this, void 0, void 0, function* () {
            if (AppState.appointmentDetail.isLocal && guestIds) {
                const contactIds = Array.from(new Set(guestIds));
                const allContacts = contactIds.map((contactId) => __awaiter(this, void 0, void 0, function* () {
                    try {
                        // For each contactId, make an API request to get contact details and store them in 'guestsDetails.value'.
                        const response = yield ContactService.getContactDetails(contactId);
                        return new Contact(response.data.contact);
                    }
                    catch (error) {
                        return new Contact({});
                    }
                }));
                const contacts = yield Promise.all(allContacts);
                AppState.guestsDetails = contacts.filter(c => c.id !== undefined);
            }
        });
        // Function to fetch details of other appointments related to the contact based on appointmentIds.
        const fetchContactAppointmentDetails = (appointmentIds) => __awaiter(this, void 0, void 0, function* () {
            if (appointmentIds) {
                const targetTimezone = getTargetTimezone();
                // For each appointmentId, make an API request to get appointment details and store them in 'appointmentsRef.value'.
                const allAppointments = appointmentIds.map(id => {
                    return CalendarEventService.getEventDetails(id, targetTimezone)
                        .then(response => new CalendarEvent(response.data.appointment))
                        .catch(_error => {
                        return new CalendarEvent({});
                    });
                });
                const appointments = yield Promise.all(allAppointments);
                // Collect userIds from all appointments to fetch user details in the next step.
                let userIds = appointments
                    .map((event) => event.bookedBy)
                    .concat(appointments.map((event) => {
                    return event.assignedUserId;
                }))
                    .filter(val => !!val);
                userIds = Array.from(new Set(userIds));
                // For each userId, make an API request to get user details and store them in 'users'.
                const allUsers = userIds.map(userId => {
                    return UserService.getUserDetails(userId)
                        .then(response => new User(response.data))
                        .catch(_error => {
                        return new User({});
                    });
                });
                const users = yield Promise.all(allUsers);
                // Collect contactIds from all appointments to fetch contact details in the next step.
                let contactIds = appointments
                    .map((event) => event.contactId)
                    .filter(val => !!val);
                contactIds = Array.from(new Set(contactIds));
                // For each contactId, make an API request to get contact details and store them in 'contacts'.
                const allContacts = contactIds.map(contactId => {
                    return ContactService.getContactDetails(contactId)
                        .then(response => new Contact(response.data.contact))
                        .catch(_error => {
                        return new Contact({});
                    });
                });
                const contacts = yield Promise.all(allContacts);
                // Merge the fetched data and create a new array of appointments with additional owner, bookedBy, and contact details.
                AppState.appointmentsRef = appointments.map(appointment => {
                    return Object.assign(Object.assign({}, appointment.data), { owner: users.find(x => x.id === appointment.assignedUserId) || {}, bookedBy: users.find(x => x.id === appointment.bookedBy) || {}, contact: contacts.find(x => x.id === appointment.contactId) || {} });
                });
            }
        });
        const onClickCloseEditAppointment = () => {
            AppState.showEditAppointmentModal = !AppState.showEditAppointmentModal;
            props.onCancelClick(true);
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(UIContentWrap), {
                locale: _unref(GHLLanguageKeysMapper)[_unref(AppState).locale]
            }, {
                default: _withCtx(() => [
                    _createVNode(ViewAppointment, {
                        router: __props.router,
                        "show-modal": __props.showModal,
                        "on-cancel-click": __props.onCancelClick,
                        "is-loading": isLoading.value
                    }, null, 8, ["router", "show-modal", "on-cancel-click", "is-loading"]),
                    (_unref(AppState).viewActivityLog)
                        ? (_openBlock(), _createBlock(ViewActivityLog, { key: 0 }))
                        : _createCommentVNode("", true),
                    (_unref(AppState).showEditAppointmentModal)
                        ? (_openBlock(), _createBlock(BookAppointment, {
                            key: 1,
                            "show-modal": _unref(AppState).showEditAppointmentModal,
                            route: __props.route,
                            router: __props.router,
                            "is-edit": true,
                            "on-cancel-click": onClickCloseEditAppointment,
                            locale: _unref(AppState).locale,
                            data: JSON.stringify(_unref(AppState).editAppointmentData),
                            "appointment-details": _unref(AppState).appointmentDetail
                        }, null, 8, ["show-modal", "route", "router", "locale", "data", "appointment-details"]))
                        : _createCommentVNode("", true)
                ]),
                _: 1
            }, 8, ["locale"]));
        };
    }
});
